import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, X, Check } from 'lucide-react';
import { useLang } from '../../../context/LangContext';

const comparisonRows = [
  { capKey: 'compare.row1.cap', beforeKey: 'compare.row1.before', afterKey: 'compare.row1.after' },
  { capKey: 'compare.row2.cap', beforeKey: 'compare.row2.before', afterKey: 'compare.row2.after' },
  { capKey: 'compare.row3.cap', beforeKey: 'compare.row3.before', afterKey: 'compare.row3.after' },
  { capKey: 'compare.row4.cap', beforeKey: 'compare.row4.before', afterKey: 'compare.row4.after' },
  { capKey: 'compare.row5.cap', beforeKey: 'compare.row5.before', afterKey: 'compare.row5.after' },
  { capKey: 'compare.row6.cap', beforeKey: 'compare.row6.before', afterKey: 'compare.row6.after' },
  { capKey: 'compare.row7.cap', beforeKey: 'compare.row7.before', afterKey: 'compare.row7.after' },
  { capKey: 'compare.row8.cap', beforeKey: 'compare.row8.before', afterKey: 'compare.row8.after' },
];

export const ComparisonTable = () => {
  const { t } = useLang();

  return (
    <section data-testid="comparison-section" className="py-24 bg-[#020209]">
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        {/* Header */}
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-4 leading-tight">
            {t("compare.title")}
          </h2>
          <p className="text-neutral-400 text-base md:text-lg max-w-3xl mx-auto">
            {t("compare.subtitle")}
          </p>
        </motion.div>

        {/* Table - Desktop */}
        <motion.div
          className="hidden md:block rounded-2xl overflow-hidden border border-white/[0.08]"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {/* Table Header */}
          <div className="grid grid-cols-3 bg-white/[0.02]">
            <div className="p-5 border-r border-white/[0.05] bg-neutral-900/50">
              <span className="text-sm font-semibold text-neutral-300">{t("compare.col.capability")}</span>
            </div>
            <div className="p-5 border-r border-white/[0.05] bg-neutral-950">
              <span className="text-sm font-semibold text-neutral-500">{t("compare.col.before")}</span>
            </div>
            <div className="p-5 bg-brand-green/10">
              <span className="text-sm font-semibold text-brand-green">{t("compare.col.after")}</span>
            </div>
          </div>

          {/* Table Rows */}
          {comparisonRows.map((row, i) => (
            <div key={i} className="grid grid-cols-3 border-t border-white/[0.05]">
              <div className="p-5 border-r border-white/[0.05] bg-neutral-900/30">
                <span className="text-sm font-medium text-white">{t(row.capKey)}</span>
              </div>
              <div className="p-5 border-r border-white/[0.05] bg-neutral-950/50 flex items-start gap-2">
                <X size={16} className="text-neutral-600 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-neutral-500">{t(row.beforeKey)}</span>
              </div>
              <div className="p-5 bg-brand-green/5 flex items-start gap-2">
                <Check size={16} className="text-brand-green flex-shrink-0 mt-0.5" />
                <span className="text-sm text-neutral-300">{t(row.afterKey)}</span>
              </div>
            </div>
          ))}
        </motion.div>

        {/* Table - Mobile (Card-based) */}
        <div className="md:hidden space-y-4">
          {comparisonRows.map((row, i) => (
            <motion.div
              key={i}
              className="rounded-xl overflow-hidden border border-white/[0.08]"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
            >
              <div className="p-4 bg-neutral-900/50 border-b border-white/[0.05]">
                <span className="text-sm font-semibold text-white">{t(row.capKey)}</span>
              </div>
              <div className="p-4 bg-neutral-950/50 border-b border-white/[0.05]">
                <div className="flex items-start gap-2">
                  <X size={14} className="text-neutral-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="text-xs text-neutral-600 block mb-1">{t("compare.col.before")}</span>
                    <span className="text-sm text-neutral-500">{t(row.beforeKey)}</span>
                  </div>
                </div>
              </div>
              <div className="p-4 bg-brand-green/5">
                <div className="flex items-start gap-2">
                  <Check size={14} className="text-brand-green flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="text-xs text-brand-green block mb-1">{t("compare.col.after")}</span>
                    <span className="text-sm text-neutral-300">{t(row.afterKey)}</span>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Conclusion + CTA */}
        <motion.div
          className="mt-12 text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <p className="text-neutral-400 text-base md:text-lg mb-8 max-w-2xl mx-auto">
            {t("compare.conclusion")}
          </p>
          <Link
            to="/agent"
            data-testid="compare-cta"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-gradient-to-r from-brand-green to-brand-blue text-white font-semibold transition-all hover:shadow-lg hover:shadow-brand-green/20"
          >
            {t("compare.cta")}
            <ArrowRight size={18} />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};
